import { useState } from 'react';
import { Alert, Checkbox, Form, Input, Typography, message } from 'antd';
import Modal from 'antd/es/modal/Modal';
import { Authentication } from '@leedpulse-monorepo/classes';
import { isValidEmail } from '@leedpulse-monorepo/utils';
import changeUserEmail, {
  ChangeUserEmailFailure,
} from '../../../../domain/use_cases/users/changeUserEmail';

export type EmailField = 'email' | 'emailAuthentication';

const EMAIL_FIELD_LABELS: Record<EmailField, string> = {
  email: 'email',
  emailAuthentication: "email d'authentification",
};

interface ChangeUserEmailModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  field: EmailField;
  email: string;
  emailAuthentication: string;
  auth: Authentication | null;
  onSuccess: () => void;
}

const ChangeUserEmailModal: React.FC<ChangeUserEmailModalProps> = ({
  open,
  onClose,
  userId,
  field,
  email,
  emailAuthentication,
  auth,
  onSuccess,
}) => {
  const otherField: EmailField =
    field === 'email' ? 'emailAuthentication' : 'email';
  const [value, setValue] = useState(
    field === 'email' ? email : emailAuthentication
  );
  // Both fields hold the same address in the usual case, an alias (user-xxx@leedpulse.io) is the exception
  const [alsoOtherField, setAlsoOtherField] = useState(
    email === emailAuthentication
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const oCurrentEmails: Record<EmailField, string> = { email, emailAuthentication };
  const sNewEmail = value.toLowerCase().trim();
  const isValid = isValidEmail(sNewEmail);
  const isChanging =
    sNewEmail !== oCurrentEmails[field].toLowerCase() ||
    (alsoOtherField && sNewEmail !== oCurrentEmails[otherField].toLowerCase());

  const handleSubmit = async () => {
    if (!auth) return;

    if (!isValid) {
      setErrorMessage('Veuillez saisir un email valide');
      return;
    }

    if (!isChanging) {
      setErrorMessage("Le nouvel email doit être différent de l'actuel");
      return;
    }

    setErrorMessage('');
    setIsBusy(true);
    try {
      await changeUserEmail(
        userId,
        {
          [field]: sNewEmail,
          ...(alsoOtherField ? { [otherField]: sNewEmail } : {}),
        },
        auth
      );
      message.success('Email modifié avec succès');
      onSuccess();
      onClose();
    } catch (e) {
      setErrorMessage(
        e instanceof ChangeUserEmailFailure
          ? e.message
          : "Erreur lors de la modification de l'email"
      );
    }
    setIsBusy(false);
  };

  return (
    <Modal
      title={`Modifier l'${EMAIL_FIELD_LABELS[field]}`}
      centered
      open={open}
      onCancel={onClose}
      okText="Modifier"
      cancelText="Annuler"
      confirmLoading={isBusy}
      okButtonProps={{ disabled: !isValid || !isChanging }}
      onOk={handleSubmit}
    >
      {(field === 'emailAuthentication' || (otherField === 'emailAuthentication' && alsoOtherField)) &&
        <Alert message="Attention, l'email d'authentification est utilisé pour la connexion à l'application. Si vous le modifiez, l'utilisateur devra se reconnecter à l'application." type="warning" showIcon />
      }
      <Form layout="vertical" style={{ marginTop: '2rem' }}>
        <Form.Item label={`Nouvel ${EMAIL_FIELD_LABELS[field]}`}>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onPressEnter={handleSubmit}
          />
        </Form.Item>

        <Form.Item>
          <Checkbox
            checked={alsoOtherField}
            onChange={(e) => setAlsoOtherField(e.target.checked)}
          >
            {`Modifier également l'${EMAIL_FIELD_LABELS[otherField]}`}
          </Checkbox>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
              {`Actuellement : ${(otherField === 'email' ? email : emailAuthentication) || 'non renseigné'}`}
            </Typography.Text>
          </div>
        </Form.Item>

        {errorMessage && (
          <Typography.Text type="danger">{errorMessage}</Typography.Text>
        )}
      </Form>
    </Modal>
  );
};

export default ChangeUserEmailModal;
